import React, { useState, useEffect, useCallback } from 'react';
import { sendMessage, createThread, clearThread } from '../services/openaiService';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [isAnimating, setIsAnimating] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize thread when chat opens
  const initializeChat = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const threadId = await createThread();
      if (!threadId) {
        throw new Error('Failed to create thread');
      }
      setMessages([
        {
          role: 'assistant',
          content: "👋 Hi! I'm your Corvela AI assistant. How can I help you today?"
        }
      ]);
    } catch (error) {
      console.error('Failed to initialize chat:', error);
      setError(error.message);
      setMessages([
        {
          role: 'assistant',
          content: "Sorry, I'm having trouble connecting. Please try again later."
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      initializeChat();
    }
  }, [isOpen, messages.length, initializeChat]);

  // Clean up thread when chat closes
  useEffect(() => {
    if (!isOpen) {
      clearThread();
      setMessages([]);
      setError(null);
      setIsLoading(false);
    }
  }, [isOpen]);

  // Handle animation completion
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimating(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    setError(null);

    try {
      // Add empty assistant message that will be updated with streaming content
      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      // Ensure we have a valid thread before sending
      const threadId = await createThread();
      if (!threadId) {
        throw new Error('Failed to create thread');
      }

      await sendMessage(userMessage, (streamedContent) => {
        setMessages(prev => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage && lastMessage.role === 'assistant') {
            lastMessage.content = streamedContent;
          }
          return newMessages;
        });
      });
    } catch (error) {
      console.error('Chat error:', error);
      setError(error.message || 'Failed to get response from assistant');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `I apologize, but I encountered an error: ${error.message}. Please try again.` 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Animated Chat Button */}
      <div
        className="relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Animated particles */}
        <div className="absolute inset-0">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-blue-500 rounded-full animate-float"
              style={{
                animationDelay: `${i * 0.5}s`,
                left: `${Math.cos(i * 120) * 20}px`,
                top: `${Math.sin(i * 120) * 20}px`,
              }}
            />
          ))}
        </div>

        {/* Main button with gradient and animations */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`
            relative flex items-center space-x-2 px-4 py-2 rounded-full shadow-lg
            transition-all duration-300 transform hover:scale-105
            bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500
            ${isHovered ? 'shadow-xl' : 'shadow-lg'}
          `}
        >
          {/* Glowing effect */}
          <div className="absolute inset-0 rounded-full bg-white opacity-20 animate-ping" />
          
          {/* Animated Corvela Text */}
          <div className="relative inline-flex items-center">
            <div className="text-white font-bold text-xl inline-flex items-center">
              <span className="inline-block">C</span>
              <span 
                className={`inline-block overflow-hidden whitespace-nowrap ${isAnimating ? 'animate-type' : 'w-0'}`}
                style={{ maxWidth: isAnimating ? '100%' : '0' }}
              >
                orvela
              </span>
              <span 
                className={`inline-block w-0.5 h-5 bg-white ml-0.5 ${isAnimating ? 'animate-blink' : 'hidden'}`}
              ></span>
            </div>
          </div>

          {/* Text with fade-in animation */}
          <span className={`
            hidden md:inline text-white font-medium
            transition-all duration-300
            ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}
          `}>
            Ask anything about Corvela
          </span>
        </button>

        {/* Preview tooltip on hover */}
        {isHovered && !isOpen && (
          <div className="absolute bottom-full right-0 mb-2 w-64 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-lg transform transition-all duration-300 animate-fade-in">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Click to chat with our AI assistant about Corvela's products and services
            </p>
          </div>
        )}
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-20 right-4 w-full max-w-md h-[500px] bg-white dark:bg-gray-800 rounded-lg shadow-xl flex flex-col transform transition-all duration-300 animate-slide-up">
          {/* Chat Header */}
          <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center bg-gradient-to-r from-blue-500 to-purple-500 rounded-t-lg">
            <div className="flex items-center space-x-2">
              <div className="relative">
                <div className="absolute inset-0 bg-white rounded-full animate-ping opacity-20" />
                <span className="text-2xl font-bold text-white">C</span>
              </div>
              <h3 className="text-lg font-semibold text-white">Chat with Corvela AI</h3>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:text-gray-200 transition-colors duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                } animate-fade-in`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                  }`}
                >
                  {message.content}
                </div>
              </div>
            ))}
            {error && (
              <div className="flex justify-start animate-fade-in">
                <div className="max-w-[80%] rounded-lg p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200">
                  <p className="font-medium">Error:</p>
                  <p>{error}</p>
                </div>
              </div>
            )}
            {isLoading && (
              <div className="flex justify-start animate-fade-in">
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Message Input */}
          <form onSubmit={handleSendMessage} className="p-4 border-t dark:border-gray-700">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                disabled={isLoading}
              />
              <button
                type="submit"
                className={`bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-2 rounded-lg transition-all duration-200 transform hover:scale-105 ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                }`}
                disabled={isLoading}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatWidget; 